import { useState, useEffect, useMemo } from 'react';
import { Column } from './index';

export const useColumnSettings = (collection: string, columns: Column[]) => {
    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (columns.length > 0) {
            const storageKey = `column_order_${collection}`;
            const visibilityKey = `column_visibility_${collection}`;

            const savedOrder = localStorage.getItem(storageKey);
            const savedVisibility = localStorage.getItem(visibilityKey);

            if (savedOrder && savedVisibility) {
                try {
                    const parsedOrder = JSON.parse(savedOrder);
                    const parsedVisibility = JSON.parse(savedVisibility);
                    const validOrder = parsedOrder.filter((key: string) =>
                        columns.some((col) => col.key === key)
                    );
                    columns.forEach((col) => {
                        if (!validOrder.includes(col.key)) validOrder.push(col.key);
                        if (!(col.key in parsedVisibility)) parsedVisibility[col.key] = !col.defaultHidden;
                    });
                    setColumnOrder(validOrder);
                    setColumnVisibility(parsedVisibility);
                } catch {
                    setColumnOrder(columns.map((col) => col.key));
                    setColumnVisibility(columns.reduce((acc, col) => ({ ...acc, [col.key]: !col.defaultHidden }), {}));
                }
            } else {
                setColumnOrder(columns.map((col) => col.key));
                setColumnVisibility(columns.reduce((acc, col) => ({ ...acc, [col.key]: !col.defaultHidden }), {}));
            }
        }
    }, [columns, collection]);

    useEffect(() => {
        if (columnOrder.length > 0) {
            localStorage.setItem(`column_order_${collection}`, JSON.stringify(columnOrder));
        }
    }, [columnOrder, collection]);

    useEffect(() => {
        if (Object.keys(columnVisibility).length > 0) {
            localStorage.setItem(`column_visibility_${collection}`, JSON.stringify(columnVisibility));
        }
    }, [columnVisibility, collection]);

    return { columnOrder, setColumnOrder, columnVisibility, setColumnVisibility };
};

export const useFilteredData = (
    data: any[],
    columns: Column[],
    columnFilters: Record<string, string>,
    searchTerm: string,
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null,
    onDataFetch?: any
) => {
    return useMemo(() => {
        // Always apply local filtering/sorting on the data we have
        // Backend fetch is for pagination, local filtering enhances it
        let result = [...data];

        // Helper to stringify all values in an object recursively
        const getAllValues = (obj: any): string => {
            if (obj === null || obj === undefined) return '';
            if (typeof obj !== 'object') return String(obj);
            return Object.values(obj).map(v => getAllValues(v)).join(' ');
        };

        // Column filters - search in specific column values
        if (Object.keys(columnFilters).length > 0) {
            const hasAnyFilter = Object.values(columnFilters).some(v => v);
            if (hasAnyFilter) {
                result = result.filter((item) => {
                    return Object.entries(columnFilters).every(([key, filterValue]) => {
                        if (!filterValue) return true;

                        // Get the column value
                        const columnValue = item[key];

                        // Find column definition to check type
                        const column = columns.find(c => c.key === key);

                        // Convert to string for comparison - handle dates specially
                        let valueStr = '';
                        if (columnValue !== null && columnValue !== undefined) {
                            if (column?.type === 'date' && columnValue) {
                                // Format date to multiple formats for flexible matching
                                try {
                                    const date = new Date(columnValue);
                                    if (!isNaN(date.getTime())) {
                                        // Include multiple formats: TR (29.01.2026), US (1/29/2026), ISO (2026-01-29)
                                        const trFormat = date.toLocaleDateString('tr-TR'); // 29.01.2026
                                        const usFormat = date.toLocaleDateString('en-US'); // 1/29/2026
                                        valueStr = `${trFormat} ${usFormat} ${columnValue}`.toLowerCase();
                                    } else {
                                        valueStr = String(columnValue).toLowerCase();
                                    }
                                } catch {
                                    valueStr = String(columnValue).toLowerCase();
                                }
                            } else {
                                valueStr = String(columnValue).toLowerCase();
                            }
                        }

                        return valueStr.includes(filterValue.toLowerCase());
                    });
                });
            }
        }

        // Global search - search in all stringified values
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter((item) => {
                const allText = getAllValues(item).toLowerCase();
                return allText.includes(term);
            });
        }

        // Sorting
        if (sortConfig) {
            result.sort((a, b) => {
                const aVal = sortConfig.key.includes('.')
                    ? sortConfig.key.split('.').reduce((obj, k) => obj?.[k], a)
                    : a[sortConfig.key];
                const bVal = sortConfig.key.includes('.')
                    ? sortConfig.key.split('.').reduce((obj, k) => obj?.[k], b)
                    : b[sortConfig.key];
                if (aVal === bVal) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                const cmp = aVal < bVal ? -1 : 1;
                return sortConfig.direction === 'asc' ? cmp : -cmp;
            });
        }

        return result;
    }, [data, columnFilters, searchTerm, sortConfig, columns]);
};
